import VisualizadorPdfComponent from '@/components/visualizador-pdf/visualizador-pdf.vue';
import { EstadoDocumentoFormalizacion } from '@/shared/model/enumerations/estado-documentos-formalizacion.model';
import { EstadoSolicitud } from '@/shared/model/enumerations/estado-solicitud.model';
import { TipoAccion } from '@/shared/model/enumerations/tipo-accion.model';
import { TipoDocumentosFormalizacion } from '@/shared/model/enumerations/tipo-documentos-formalizacion';
import { IPersona, Persona } from '@/shared/model/persona/persona.model';
import { TransicionContext } from '@/shared/model/proceso/transicion-context.model';
import { Transicion } from '@/shared/model/proceso/transicion.model';
import { Solicitud } from '@/shared/model/solicitud.model';
import PersonaService from '@/shared/persona/persona.service';
import { BForm, BModal, BvModalEvent } from 'bootstrap-vue';
import { mixins } from 'vue-class-component';
import { Component, Inject, Ref } from 'vue-property-decorator';
import GenerarPdfService from '../generarPdf/generar-pdf.service';
import SolicitudMixin from './solicitud-mixin';

@Component({
  components: {
    'visualizador-pdf': VisualizadorPdfComponent,
  },
})
export default class GenerarPdfMixin extends mixins(SolicitudMixin) {
  @Inject('generarPdfService') public generarPdfService: () => GenerarPdfService;
  @Inject('personaService') public personaService: () => PersonaService;

  public numeroCvu = this.$store.getters.account?.id ?? '';

  /**
   * Indica si se debe de mostrar el spinner/overlay.
   */
  public show = false;
  public pdfReady = false;
  public pdfFirma = false;
  public contenidoBase64 = '';

  public bvModalEvent: BvModalEvent;

  public tipoDocumentoOptions = Object.values(TipoDocumentosFormalizacion);
  public tipoDocumento: TipoDocumentosFormalizacion = null;

  public perfil: IPersona = new Persona();

  /**
   * Indica cuantos documentos se van a procesar.
   */
  public aProcesar = 0;

  /**
   * Indica el total general de documentos a firmar.
   */
  public totalGeneralAFirmar = 0;

  /**
   * Indica cuantos documentos han sido procesados.
   */
  public procesados = 0;

  /**
   * Indica cuantos errores han sido procesados.
   */
  public errorProcesados = 0;

  /**
   * Contraseña para crear la firma del pdf
   */
  public password = '';

  /**
   * Indica si se debe mostrar la contraseña o no.
   */
  public mostrarPassword = false;

  /**
   * Indica si se debe mostrar la leyenda de que el campo es requerido.
   */
  public estadoPassword = false;

  /**
   * Indica cuantos registros se van a procesar.
   */
  public totalRegistros = 0;

  /**
   * CVU del solicitante
   */
  public cvuSolicitante = '';

  @Ref('form')
  public readonly form: BForm;

  @Ref('modal')
  public readonly modal: BModal;
  /**
   * Indica si el proceso de firma es masivo
   */
  public masivo = false;

  @Ref('formsMasivo')
  public readonly formsMasivo: BForm;

  @Ref('modalMasivo')
  public readonly modalMasivo: BModal;

  @Ref('modalCancelarDocumento')
  public readonly modalCancelarDocumento: BModal;

  /**
   * Genera el documento pdf y lo muestra en el visualizador-pdf
   */
  public async generarVisualizar(id: string) {
    try {
      this.show = true; //Activa overlay
      this.pdfReady = false;
      const responseDocumento = await this.generarPdfService().exportarPdfFormalizacion(id, null, this.tipoDocumento);
      this.contenidoBase64 = `data:application/pdf;base64,${responseDocumento.contenidoBase64}`;
      this.show = false;
      this.pdfReady = true;
    } catch (error) {
      this.show = false;
      this.alertService().showError(this, this.$t(error.response.data.detail, 'Error al generar vista previa documento').toString());
      return Promise.reject(error);
    }
  }

  /**
   * Genera documentos y guarda en el DMS
   */
  public generarFirma(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null, password: string, tipoPlantilla: TipoDocumentosFormalizacion) => Promise<any>,
    id: string | null,
    password: string,
    tipoPlantilla: TipoDocumentosFormalizacion,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();
    const valido = this.form.checkValidity();
    this.estadoPassword = valido;

    if (!valido) {
      return;
    }

    this.$nextTick(() => {
      this.modal.hide();
    });

    serviceMethod(id, password, tipoPlantilla)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.cvuSolicitante = '';
        this.estadoPassword = null;
      });
  }

  /**
   * Genera documentos y guarda en el DMS de manera masiva
   */
  public generarFirmaMasiva(
    bvModalEvent: BvModalEvent,
    serviceMethod: (
      id: string | null,
      password: string,
      curp: string,
      nombre: string,
      tipoPlantilla: TipoDocumentosFormalizacion
    ) => Promise<any>,
    id: string | null,
    password: string,
    curp: string,
    nombre: string,
    tipoPlantilla: TipoDocumentosFormalizacion,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();
    const valido = this.form.checkValidity();
    this.estadoPassword = valido;

    if (!valido) {
      return;
    }

    this.$nextTick(() => {
      this.modal.hide();
    });

    serviceMethod(id, password, curp, nombre, tipoPlantilla)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.cvuSolicitante = '';
        this.estadoPassword = null;
      });
  }

  /**
   * Establece el password para generar documentos para las solicitudes
   *
   * @memberof BandejaOficiosYReconocimientosComponent
   */
  public establecerPasswordDocumentos(): void {
    this.password = null;
    this.modal.show();
    this.masivo = true;
  }

  /**
   * Mostrar modal para establecer el password para generar documentos individualmente.
   *
   * @memberof BandejaOficiosYReconocimientosComponent
   */
  public mostrarModalPasswordDocumentoIndividual(): void {
    this.modal.show();
  }

  /**
   * Establece el password para generar un documentos de una solicitud
   * @param solicitud La solicitud a la que se le va a generar el documento.
   * @param cvu El cvu del solicitante.
   *
   * @memberof BandejaOficiosYReconocimientosComponent
   */
  public establecerPasswordDocumento(solicitud: Solicitud, cvu: string): void {
    this.password = null;
    this.modal.show();
    this.solicitud = solicitud;
    this.cvuSolicitante = cvu;
    this.masivo = false;
  }
  /**
   * Genera documentos y guarda en el DMS masivamente
   */
  public generarFirmaMasivo(
    bvModalEvent: BvModalEvent,
    serviceMethod: (
      password: string,
      tipoPlantilla: TipoDocumentosFormalizacion,
      estado: EstadoDocumentoFormalizacion,
      solucionId: string,
      estadoSolicitud: EstadoSolicitud
    ) => Promise<any>,
    password: string,
    tipoPlantilla: TipoDocumentosFormalizacion,
    estado: EstadoDocumentoFormalizacion,
    solucionId: string,
    estadoSolicitud: EstadoSolicitud,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();
    const valido = this.formsMasivo.checkValidity();
    this.estadoPassword = valido;

    if (!valido) {
      return;
    }

    this.$nextTick(() => {
      this.modalMasivo.hide();
    });

    this.show = true;
    this.aProcesar = 1;

    serviceMethod(password, tipoPlantilla, estado, solucionId, estadoSolicitud)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
        this.procesados = 1;
        this.cvuSolicitante = '';
        this.estadoPassword = null;
      });
  }

  /**
   * Actualiza una solicitud a estado FORMALIZADA
   *
   * @param solicitud Solicitud a formalizar
   */
  public async formalizarSolicitud(solicitud: Solicitud): Promise<Solicitud> {
    const transicionContext = new TransicionContext();
    transicionContext.solicitudId = solicitud.id;
    transicionContext.solucionId = solicitud.solucionId;
    const transicion = new Transicion(TipoAccion.FORMALIZAR);
    transicionContext.transicion = transicion;
    try {
      return await this.solicitudService().transition(transicionContext);
    } catch (error) {
      if (!this.masivo) {
        this.show = false;
        this.alertService().showHttpError(this, error.response);
        throw error;
      }
    }
  }

  /**
   * Muestra un modal para cancelar un documento.
   *
   * @memberof CamposConocimientoComponent
   */
  public mostrarModalCancelarDocumento(solicitud: Solicitud): void {
    this.modalCancelarDocumento.show();
    this.solicitud = solicitud;
  }

  /**
   *  Cancela un documento.
   */
  public cancelarDocumento(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null, tipoPlantilla: TipoDocumentosFormalizacion, estado: EstadoDocumentoFormalizacion) => Promise<any>,
    id: string | null,
    tipoPlantilla: TipoDocumentosFormalizacion,
    estado: EstadoDocumentoFormalizacion,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modalCancelarDocumento.hide();
    });

    this.show = true;

    serviceMethod(id, tipoPlantilla, estado)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
      });
  }

  /**
   *  Cancela todos los documentos.
   */
  public cancelarDocumentos(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null) => Promise<any>,
    id: string | null,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modalCancelarDocumento.hide();
    });

    this.show = true;

    serviceMethod(id)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
      });
  }

  /**
   * Regenera documentos y guarda en el DMS
   */
  public generarFirmaRegenerada(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null, tipoPlantilla: TipoDocumentosFormalizacion) => Promise<any>,
    id: string | null,
    tipoPlantilla: TipoDocumentosFormalizacion,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modal.hide();
    });

    serviceMethod(id, tipoPlantilla)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.cvuSolicitante = '';
        this.estadoPassword = null;
      });
  }

  /**
   * Genera el documento pdf y lo muestra en el visualizador-pdf regenerado
   */
  public async generarVisualizarRegenerado(id: string) {
    try {
      this.show = true;
      this.pdfReady = false;
      const responseDocumento = await this.generarPdfService().exportarPdfFormalizacionRegenerado(id, this.tipoDocumento);
      this.contenidoBase64 = `data:application/pdf;base64,${responseDocumento.contenidoBase64}`;
      this.show = false;
      this.pdfReady = true;
    } catch (error) {
      this.show = false;
      this.alertService().showError(this, this.$t(error.response.data.detail, 'Error al generar vista previa documento').toString());
      return Promise.reject(error);
    }
  }

  /**
   *  Cancela un documento y agrega sello de cancelado.
   */
  public cancelarDocumentoSelloCancelado(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null, tipoDocumento: TipoDocumentosFormalizacion, numeroCvu: string) => Promise<any>,
    id: string | null,
    tipoDocumento: TipoDocumentosFormalizacion,
    numeroCvu: string,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modalCancelarDocumento.hide();
    });

    this.show = true;

    serviceMethod(id, tipoDocumento, numeroCvu)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
      });
  }

  /**
   *  Cancela los documentos y agrega sello de cancelado.
   */
  public cancelarDocumentosSelloCancelado(
    bvModalEvent: BvModalEvent,
    serviceMethod: (id: string | null, numeroCvu: string) => Promise<any>,
    id: string | null,
    numeroCvu: string,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modalCancelarDocumento.hide();
    });

    this.show = true;

    serviceMethod(id, numeroCvu)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
      });
  }

  /**
   *  Genera un documento cancelado.
   */
  public generarDocumentoCancelado(
    bvModalEvent: BvModalEvent,
    serviceMethod: (solicitudId: string | null, tipoPlantilla: TipoDocumentosFormalizacion, numeroCvu: string) => Promise<any>,
    solicitudId: string | null,
    tipoPlantilla: TipoDocumentosFormalizacion,
    numeroCvu: string,
    onSuccess: (result: any) => void,
    onError?: (error: any) => void
  ): void {
    bvModalEvent.preventDefault();

    this.$nextTick(() => {
      this.modalCancelarDocumento.hide();
    });

    this.show = true;

    serviceMethod(solicitudId, tipoPlantilla, numeroCvu)
      .then(result => {
        onSuccess(result);
      })
      .catch(error => {
        if (onError) {
          onError(error);
        } else {
          this.alertService().showHttpError(this, error.response);
        }
      })
      .finally(() => {
        this.show = false;
      });
  }

  /**
   * Consulta informacion del perfil dado un cvu
   */
  public async buscarCVU(): Promise<void> {
    try {
      const res = await this.personaService().findByCvu(this.numeroCvu);

      this.perfil.curp = res?.curp;
      this.perfil.nombre = res.nombre + ' ' + res.apellidoPaterno + ' ' + res.apellidoMaterno;
    } catch (error) {
      this.alertService().showHttpError(this, error.response);
      throw error;
    }
  }
}
